import { ChevronDown, Undo2 } from 'lucide-react';
import { Badge, Button, Popover, PopoverContent, PopoverTrigger } from '@eo/shared';

import type { TodaysDecisionsProps } from './types';

export function TodaysDecisions({ entries, busyId, onRevert }: TodaysDecisionsProps) {
  if (entries.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {/* Wears the control chrome the date picker wears: it sits in the same
            row, so anything less reads as loose text rather than a control. */}
        <button
          type="button"
          className="flex h-8 items-center gap-2 rounded-8 border border-control-border bg-control px-3 text-sm text-control-foreground transition-colors hover:bg-control-hover focus-visible:border-focus focus-visible:outline-none"
        >
          Today’s decisions ({entries.length})
          <ChevronDown className="size-4 shrink-0 text-control-placeholder" />
        </button>
      </PopoverTrigger>
      {/* Rows own their inset so a hover fills the popover edge to edge. */}
      <PopoverContent align="start" className="w-80 p-1">
        <ul className="flex flex-col divide-y divide-border-faint">
          {entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-start gap-2 px-2 py-2 text-sm transition-colors hover:bg-control-hover"
            >
              <Badge
                variant={entry.action === 'approved' ? 'success' : 'muted'}
                className="mt-0.5 shrink-0"
              >
                {entry.action}
              </Badge>
              {/* Stacked, never inline: side by side the client only dropped to
                  its own line once a name ran long, so no two rows agreed. */}
              <div className="flex min-w-0 flex-col">
                {entry.clickupTaskId ? (
                  <a
                    href={`https://app.clickup.com/t/${entry.clickupTaskId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-text-primary underline-offset-2 hover:underline"
                  >
                    {entry.taskName}
                  </a>
                ) : (
                  <span className="font-medium text-text-primary">{entry.taskName}</span>
                )}
                {entry.client && <span className="text-xs text-text-tertiary">{entry.client}</span>}
              </div>
              {/* Either decision comes back: an approval's ClickUp task is
                  archived, not deleted, so undoing it loses nothing. */}
              {entry.taskId && (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Return to the board"
                  aria-label={`Return ${entry.taskName} to the board`}
                  disabled={busyId !== null}
                  onClick={() => onRevert(entry)}
                  className="ml-auto shrink-0 text-text-tertiary hover:text-text-primary"
                >
                  <Undo2 className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
